import { menu } from 'constants/menu';
import { i18n } from 'i18n-config';
import { LogoGreenClipArt } from 'lib/ClipArts';
import {
  SocialFacebookIcon,
  SocialLinkedinIcon,
  SocialTwitterIcon,
  WorldBlueIcon
} from 'lib/Icons';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import { FC, PropsWithChildren } from 'react';
import { cn } from 'utils/cn';
import Dropdown from './Dropdown';
import Typography from './Typography';

const Footer: FC<PropsWithChildren<unknown>> = ({}) => {
  const t = useTranslations();
  const locale = useLocale();

  return (
    <footer className="inline-flex w-full flex-col items-center justify-start bg-tolq-gray-200">
      <div className={cn('block w-full max-w-6xl p-6 sm:p-16', 'lg:p-0 lg:px-16')}>
        <div
          className={cn(
            'grid grid-cols-2 gap-4 gap-y-6',
            'sm:pt-0 lg:grid-cols-4 lg:flex-col lg:gap-y-8 lg:pb-6 lg:pt-10'
          )}
        >
          <LogoGreenClipArt
            className={cn(
              'order-first col-span-1 w-[100px]',
              'lg:col-span-2 lg:w-[160px] lg:scale-100'
            )}
          />
          <div
            className={cn(
              'jus order-2 col-span-1 flex items-center',
              'lg:order-3 lg:col-span-2 lg:justify-start'
            )}
          >
            <Dropdown
              items={i18n.locales.map((locale) => ({
                name: t(`locales.${locale}`),
                href: `/${locale}`,
                locale: locale
              }))}
            >
              <span className="flex items-center gap-2">
                <WorldBlueIcon />
                <Typography variant="medium3" className=" text-tolq-blue-100">
                  {t(`locales.${locale}`)}
                </Typography>
              </span>
            </Dropdown>
            <Typography
              variant="light3"
              className={cn('hidden w-full text-center text-tolq-blue-100', 'lg:block')}
            >
              © {new Date().getFullYear()} Tolq.com BV - {t('footer.all_rights')}.
            </Typography>
          </div>
          <div className={cn('order-3 col-span-2 grid grid-cols-subgrid gap-4', 'lg:order-2')}>
            <div
              className={cn('col-span-4 grid grid-cols-subgrid gap-4 gap-y-6', 'lg:grid-cols-4')}
            >
              {menu.map((path) => (
                <Typography
                  key={path}
                  variant="light1"
                  className={cn('col-span-1 text-tolq-black-200', 'lg:text-end')}
                >
                  <Link href={`/${path}`}>{t(`navbar.${path}`)}</Link>
                </Typography>
              ))}
            </div>
          </div>
          <div
            className={cn(
              'order-4 col-span-2 grid grid-cols-subgrid items-center',
              'lg:order-5 lg:flex lg:justify-end lg:gap-8'
            )}
          >
            <a href="/privacy-policy" target="_blank" className="col-span-1">
              <Typography variant="light3" className="text-tolq-blue-100 ">
                {t('footer.privacy')}
              </Typography>
            </a>
            <a href="/terms-conditions" target="_blank" className="col-span-1">
              <Typography variant="light3" className="text-tolq-blue-100">
                {t('footer.terms')}
              </Typography>
            </a>
            <a href="/twitter" target="_blank" className={cn('col-span-1 hidden', 'lg:block')}>
              <SocialTwitterIcon className="!text-tolq-blue-100" />
            </a>
            <a href="/linkedin" target="_blank" className={cn('col-span-1 hidden', 'lg:block')}>
              <SocialLinkedinIcon className="!text-tolq-blue-100" />
            </a>
            <a href="/facebook" target="_blank" className={cn('col-span-1 hidden', 'lg:block')}>
              <SocialFacebookIcon className="!text-tolq-blue-100" />
            </a>
          </div>
          <div className={cn('order-5 col-span-2 flex items-center py-4', 'lg:hidden')}>
            <Typography variant="light3" className="w-full text-center text-tolq-blue-100">
              © {new Date().getFullYear()} Tolq.com BV - {t('footer.all_rights')}.
            </Typography>
          </div>
          <div className={cn('order-6 col-span-2 flex items-center justify-center', 'lg:hidden')}>
            <div className="flex gap-8">
              <a href="/twitter" target="_blank" className="col-span-1">
                <SocialTwitterIcon className="!text-tolq-blue-100" />
              </a>
              <a href="/linkedin" target="_blank" className="col-span-1">
                <SocialLinkedinIcon className="!text-tolq-blue-100" />
              </a>
              <a href="/facebook" target="_blank" className="col-span-1">
                <SocialFacebookIcon className="!text-tolq-blue-100" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
